import { parseDay } from '@map-my-money/engine';
import { nextPayday } from '@map-my-money/views';
import { useEffect, useId, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { t } from '../i18n/index.ts';
import { todayString } from '../lib/clock.ts';
import { dayMonth } from '../lib/format.ts';
import { useMarks } from '../lib/useMarks.ts';
import { usePlanNotice } from '../state/notices.ts';
import { usePlayground } from '../state/playground.ts';
import { useActivePlan, useStore } from '../state/store.tsx';
import { Icon } from './Icon.tsx';
import { PeopleDialog } from './PlanPeople.tsx';

const START_SUMMARY = 'Started from Start';
const DRAFT_KEY = 'mm.start.draft';

/** A Start draft still in this browser (made before signing in): how many rows it would add. */
function draftRows(): number {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    const draft = raw ? (JSON.parse(raw) as { rows?: { ticked?: boolean }[]; savedAt?: string }) : null;
    if (!draft?.savedAt || Date.now() - Date.parse(draft.savedAt) > 30 * 86_400_000) return 0;
    return (draft.rows ?? []).filter((r) => r.ticked).length;
  } catch {
    return 0;
  }
}

interface Step {
  readonly key: string;
  readonly label: string;
  readonly done: boolean;
  readonly action?: { readonly label: string; readonly to?: string; readonly onClick?: () => void };
}

/**
 * Home's "Finish your map" (Start, after paying): what a full map still needs, ticked off as it
 * happens. It goes when everything is done or the person hides it. Also offers a Start map left in
 * this browser to a signed-in plan.
 */
export function FinishMap({ onCheckIn, className = '' }: { onCheckIn: () => void; className?: string }) {
  const { plan, waypoints, versions } = useStore();
  const active = useActivePlan();
  const playground = usePlayground() !== null;
  const marks = useMarks();
  const { dismissed, dismiss } = usePlanNotice('finish-map');
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [inviting, setInviting] = useState(params.get('invite') === '1');
  const [leftover, setLeftover] = useState(0);
  const id = useId();
  useEffect(() => setLeftover(draftRows()), []);
  if (playground || !plan) return null;

  const saved = params.get('start') === 'saved';
  const payday = saved ? nextPayday(plan, todayString()) : null;
  const closeSaved = () => {
    const next = new URLSearchParams(params);
    next.delete('start');
    setParams(next, { replace: true });
  };
  const fromStart = saved || versions.some((v) => v.summary.startsWith(START_SUMMARY)) || marks.size > 0;
  const transactions = plan.transactions ?? [];
  const investments = transactions.filter((t) => t.type === 'investment');
  const examples = [...marks.values()].filter((m) => m === 'example').length;
  const checks = [...marks.values()].filter((m) => m === 'check').length;
  const steps: Step[] = [
    { key: 'payments', label: t('finish.payments'), done: transactions.length > 0, action: { label: t('finish.add'), to: '/start' } },
    { key: 'balances', label: t('finish.balances'), done: waypoints.length > 0, action: { label: t('finish.checkIn'), onClick: onCheckIn } },
    { key: 'goal', label: t('finish.goal'), done: (plan.goals ?? []).length > 0, action: { label: t('finish.add'), to: '/goals' } },
    ...(investments.length > 0
      ? [{ key: 'pension', label: t('finish.pension'), done: investments.every((i) => Number(i.initialBalance ?? 0) > 0), action: { label: t('finish.add'), onClick: onCheckIn } }]
      : []),
    ...(examples > 0 ? [{ key: 'examples', label: examples === 1 ? t('finish.examplesOne') : t('finish.examples', { n: examples }), done: false, action: { label: t('finish.review'), to: '/plan' } }] : []),
    ...(checks > 0 ? [{ key: 'checks', label: checks === 1 ? t('finish.checksOne') : t('finish.checks', { n: checks }), done: false, action: { label: t('finish.review'), to: '/plan' } }] : []),
    ...(active.plan && active.isOwner && (active.plan.memberCount ?? 0) === 0 && params.get('invite') === '1'
      ? [{ key: 'invite', label: t('finish.invite'), done: false, action: { label: t('finish.add'), onClick: () => setInviting(true) } }]
      : []),
    { key: 'assistant', label: t('finish.assistant'), done: false, action: { label: t('finish.connect'), to: '/connections' } },
  ];
  const counted = steps.filter((s) => s.key !== 'assistant');
  const done = counted.filter((s) => s.done).length;
  const show = fromStart && dismissed === false && done < counted.length;

  return (
    <>
      {saved && (
        <section className="welcome-note finish-saved" role="status" aria-labelledby={`${id}-saved`} data-testid="start-saved">
          <span className="welcome-note__icon" aria-hidden="true"><Icon name="checkCircle" size={17} /></span>
          <div className="welcome-note__body">
            <h2 className="welcome-note__title" id={`${id}-saved`}>{t('finish.saved.title')}</h2>
            <p className="welcome-note__text">{payday ? t('finish.saved.body', { date: dayMonth(parseDay(payday)) }) : t('finish.saved.bodyNoPay')}</p>
          </div>
          <button type="button" className="welcome-note__close" aria-label={t('welcome.note.dismiss')} onClick={closeSaved}><Icon name="close" size={16} /></button>
        </section>
      )}
      {leftover > 0 && active.canEdit && (
        <section className={`card card--pad-lg finish-draft ${className}`} aria-labelledby={`${id}-draft`} data-testid="start-leftover">
          <h2 className="section-title" id={`${id}-draft`}>{t('finish.draft.title')}</h2>
          <p className="muted-2">{t('finish.draft.body', { n: leftover })}</p>
          <div className="button-row">
            <button type="button" className="button button--sm button--primary" onClick={() => navigate('/start/map?view=list')}>{t('finish.draft.cta')}</button>
            <button type="button" className="button button--sm button--ghost" onClick={() => {
              try { localStorage.removeItem(DRAFT_KEY); } catch { /* storage may be unavailable */ }
              setLeftover(0);
            }}>{t('finish.draft.discard')}</button>
          </div>
        </section>
      )}
      {show && (
        <section className={`card card--pad-lg finish ${className}`} aria-labelledby={id} data-testid="finish-map">
          <div className="finish__head">
            <h2 className="section-title" id={id}>{t('finish.title')} <span className="finish__count">· {t('finish.count', { done, total: counted.length })}</span></h2>
            <button type="button" className="text-button" onClick={dismiss}>{t('finish.dismiss')}</button>
          </div>
          <div className="progress finish__bar" role="progressbar" aria-label={t('finish.title')} aria-valuemin={0} aria-valuemax={counted.length} aria-valuenow={done}>
            <div className="progress__fill" style={{ width: `${(done / counted.length) * 100}%` }} />
          </div>
          <ul className="finish__list">
            {steps.map((s) => (
              <li key={s.key} className={`finish__item ${s.done ? 'is-done' : ''}`} data-testid={`finish-${s.key}`}>
                <span className="finish__tick" aria-hidden="true">{s.done ? <Icon name="check" size={12} strokeWidth={3} /> : null}</span>
                <span className="finish__label">{s.label}{s.done && <span className="sr-only"> ✓</span>}</span>
                {!s.done && s.action && (s.action.to
                  ? <Link to={s.action.to} className="text-link">{s.action.label}</Link>
                  : <button type="button" className="link-button finish__action" onClick={s.action.onClick}>{s.action.label}</button>)}
              </li>
            ))}
          </ul>
        </section>
      )}
      {inviting && active.plan && <PeopleDialog plan={active.plan} onClose={() => setInviting(false)} />}
    </>
  );
}
